<template>
  <!-- XOL-586 (Q10): what one field or recipient edit changed, Before and After per field.
       READ-ONLY. The Activity list carries NO values; this dialog fetches them from
       GET /actions/{id}/activity/{activity} EVERY time it opens (nothing is cached or taken
       from the list row). The header and the meta list come from the list row, so they show
       at once; only the change blocks wait for the answer.

       Delta 4: InfoDialogHeader (the row's sentence is its subtitle), InfoMetaList, and a scalar
       is two framed values (red Before, green After) with an arrow between them.

       Rich text (remarks) goes through ReadOnlyRemarks, the only safe way to show stored
       HTML here. Lists show what was added and removed, not the whole list.

       The parent owns `visible` and closes it on `close`. -->
  <el-dialog
    :visible="visible"
    :title="title"
    append-to-body
    :width="isMediumOrLess ? '92%' : '760px'"
    top="6vh"
    custom-class="xol-activity-changes-dialog xol-info-dialog"
    @open="onOpen"
    @opened="onOpened"
    @close="onDialogClose"
    @closed="onClosed">

    <info-dialog-header slot="title" ref="header"
      :icon="headerIcon"
      :title="title"
      :subtitle="summary"
      :compact="isMediumOrLess" />

    <div class="changes" ref="body" tabindex="-1">

      <info-meta-list :rows="metaRows" :narrow="isMediumOrLess" />

      <p v-if="coalesced > 1" class="changes__coalesced">
        <i class="fa fa-info-circle" aria-hidden="true"></i><span>{{text('requestActivity.changes.coalesced', { n: coalesced })}}</span>
      </p>

      <div class="changes__blocks" :class="{ 'is-loading': loading }"
        v-loading="loading" :element-loading-text="text('requestActivity.changes.loading')">

        <div v-if="error" class="changes__error">
          <el-alert type="error" show-icon :closable="false" :title="errorText" />
          <el-button v-if="error === 'generic'" size="small" class="changes__retry" @click="load">{{text('requestActivity.changes.retry')}}</el-button>
        </div>

        <p v-else-if="loaded && blocks.length === 0" class="changes__none">{{text('requestActivity.changes.none')}}</p>

        <section v-for="b in blocks" :key="b.key" class="change-block">

          <h5 class="remarks-heading change-block__label">{{b.label}}</h5>

          <div v-if="b.changedBack" class="change-block__back">
            <el-tag size="mini" type="info" effect="light"><i class="fa fa-undo" aria-hidden="true"></i><span>{{text('requestActivity.changes.changedBack')}}</span></el-tag>
            <small>{{text('requestActivity.changes.changedBack.note')}}</small>
          </div>

          <!-- A scalar: two frames with an arrow between them (the arrow points down on a phone). -->
          <div v-if="b.type === 'scalar'" class="change-grid" :class="{ 'is-narrow': isMediumOrLess }">
            <div class="change-frame is-before">
              <div class="change-label"><i class="fa fa-minus" aria-hidden="true"></i><span>{{text('requestActivity.changes.before')}}</span></div>
              <div class="change-value" :class="{ 'is-empty': b.before.empty }">{{b.before.text}}</div>
            </div>
            <div class="change-arrow" aria-hidden="true"><i class="fa" :class="isMediumOrLess ? 'fa-arrow-down' : 'fa-arrow-right'"></i></div>
            <div class="change-frame is-after">
              <div class="change-label"><i class="fa fa-plus" aria-hidden="true"></i><span>{{text('requestActivity.changes.after')}}</span></div>
              <div class="change-value" :class="{ 'is-empty': b.after.empty }">{{b.after.text}}</div>
            </div>
          </div>

          <!-- Rich text is too wide to compare side by side: stacked at every width. -->
          <template v-else-if="b.type === 'text'">
            <div class="change-label"><i class="fa fa-minus" aria-hidden="true"></i><span>{{text('requestActivity.changes.before')}}</span></div>
            <read-only-remarks class="change-remarks is-before"
              :html="b.beforeHtml"
              :aria-label="text('requestActivity.changes.remarksAria', { label: b.label, side: 'before' })"
              :empty-text="text('requestActivity.changes.empty')"
              max-height="16em" />
            <div class="change-label"><i class="fa fa-plus" aria-hidden="true"></i><span>{{text('requestActivity.changes.after')}}</span></div>
            <read-only-remarks class="change-remarks is-after"
              :html="b.afterHtml"
              :aria-label="text('requestActivity.changes.remarksAria', { label: b.label, side: 'after' })"
              :empty-text="text('requestActivity.changes.empty')"
              max-height="16em" />
          </template>

          <!-- A list: what was added and what was removed. -->
          <template v-else>
            <div v-for="g in b.groups" :key="g.key" class="change-group" :class="'is-' + g.key">
              <div class="change-group__head"><i class="fa" :class="groupIcon(g.key)" aria-hidden="true"></i><span>{{text('requestActivity.changes.groupCount', { label: g.label, n: g.items.length })}}</span></div>
              <ul class="change-list"
                :class="{ 'is-scrolling': g.items.length > 20 }"
                :tabindex="g.items.length > 20 ? 0 : null"
                :aria-label="text('requestActivity.changes.list.aria', { label: b.label, group: g.label })">
                <li v-for="(item, i) in g.items" :key="i">{{item}}</li>
              </ul>
            </div>
            <p v-if="b.noNet" class="changes__none">{{text('requestActivity.changes.noNetChange')}}</p>
          </template>

        </section>

      </div>
    </div>

    <div slot="footer" class="changes__footer">
      <el-button size="small" :class="{ 'is-block': isMediumOrLess }" @click="requestClose">{{text('requestActivity.changes.close')}}</el-button>
    </div>

  </el-dialog>
</template>

<script>
import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'
import InfoDialogHeader from '@/components/requests/InfoDialogHeader'
import InfoMetaList from '@/components/requests/InfoMetaList'
import { activityText, activityRow, changeBlocks, changesTitle } from '@/lib/requestActivity'

// The glyph in front of each list group's head.
var GROUP_ICONS = {
  added: 'fa-plus-circle',
  removed: 'fa-minus-circle',
  unsubscribed: 'fa-bell-slash',
  resubscribed: 'fa-bell',
  ticks: 'fa-exchange-alt'
}

export default {
  name: 'activity-changes-dialog',

  mixins: [DateMixins],

  components: {
    ReadOnlyRemarks,
    InfoDialogHeader,
    InfoMetaList
  },

  props: {
    // The request the entry belongs to (an entry of another request answers 404).
    actionId: {
      type: [Number, String],
      default: null
    },

    // The Activity list row that was opened (its id, kind, who, when, step). Carries no values.
    row: {
      type: Object,
      default: null
    },

    // The parent owns it and clears it on `close`.
    visible: {
      type: Boolean,
      default: false
    },

    // The request's steps, to name "Step 2: Technical Proposal".
    steps: {
      type: Array,
      default: function () { return [] }
    },

    rtx: {
      type: String,
      default: 'request'
    },

    // { towerco, owner, provider }: the short name of each party's organisation.
    orgs: {
      type: Object,
      default: function () { return {} }
    },

    // { towerCo, telco, provider }: for the recipient-list field labels.
    labelCtx: {
      type: Object,
      default: function () { return {} }
    }
  },

  data () {
    return {
      detail: null,
      loading: false,
      loaded: false,
      // null | 'generic' | 'notFound' | 'forbidden'
      error: null,
      seq: 0,
      opened: false,
      opener: null
    }
  },

  computed: {
    ctx () {
      return {
        rtx: this.rtx,
        formatDate: this.formatDate,
        formatDateTime: this.formatDateTime,
        orgs: this.orgs,
        labelCtx: this.labelCtx
      }
    },

    // The list row as the table showed it: the same sentence, who, when and step.
    shown () {
      return this.row ? activityRow(this.row, this.ctx) : null
    },

    title () {
      return changesTitle(this.row)
    },

    // The header's subtitle: the row's own sentence ("Changed Approval Remarks and Re-survey").
    summary () {
      return this.shown ? this.shown.text : ''
    },

    headerIcon () {
      return this.row && this.row.kind === 'recipients_changed' ? 'fa-address-book' : 'fa-edit'
    },

    coalesced () {
      var n = this.detail && this.detail.coalesced !== undefined ? this.detail.coalesced : (this.row ? this.row.coalesced : 1)

      return Number(n) || 1
    },

    metaRows () {
      var s = this.shown

      if (!s) {
        return []
      }

      return [
        { key: 'who', label: this.text('requestActivity.changes.meta.who'), value: s.who.text },
        { key: 'when', label: this.text('requestActivity.changes.meta.when'), value: s.when },
        { key: 'step', label: this.text('requestActivity.changes.meta.step'), value: this.stepText() }
      ]
    },

    // What the dialog is showing: null while it is closed.
    fetchKey () {
      return this.visible && this.row && this.row.id ? this.actionId + '/' + this.row.id : null
    },

    blocks () {
      return this.detail ? changeBlocks(this.detail.changes, this.row, this.ctx) : []
    },

    errorText () {
      if (this.error === 'forbidden') {
        return this.text('requestActivity.error.forbidden', { towerCo: this.labelCtx.towerCo || 'the Tower Co' })
      }

      return this.text(this.error === 'notFound' ? 'requestActivity.changes.notFound' : 'requestActivity.changes.error')
    }
  },

  watch: {
    // Every open fetches again: nothing is cached between opens. The same dialog pointed at
    // another entry while it is open fetches that one. ONE watcher on both: the Activity log
    // opens the dialog and sets its entry in the same tick, which must cost one GET, not two.
    fetchKey (now) {
      if (now) {
        this.load()
      } else {
        this.seq++
        this.opened = false
      }
    }
  },

  mounted () {
    if (this.visible) {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
      this.load()
    }
  },

  methods: {
    text (key, params) {
      return activityText(key, params)
    },

    groupIcon (key) {
      return GROUP_ICONS[key] || 'fa-circle'
    },

    // "Step 2: Technical Proposal", or "Request" for an entry that belongs to no step.
    stepText () {
      var row = this.row

      if (!row || row.step_no === null || row.step_no === undefined || row.step_no === '') {
        return this.text('requestActivity.step.request')
      }

      var title = null

      ;(this.steps || []).forEach(function (s) {
        if (Number(s.stepNo) === Number(row.step_no) && Number(s.partyNo) === 1 && s.stepName) {
          title = s.stepName
        }
      })

      return this.text('requestMail.batch.meta.stepValue', { n: row.step_no, title: title })
    },

    load () {
      var self = this
      var seq = ++this.seq

      if (!this.actionId || !this.row || !this.row.id) {
        return Promise.resolve()
      }

      this.loading = true
      this.error = null

      return axios.get(this.getEndpoint('/actions/' + this.actionId + '/activity/' + this.row.id)).then(function (response) {
        if (seq !== self.seq) {
          return
        }

        var body = response ? response.data : null

        // A body wrapped in { data: {...} } is read as the entry itself.
        if (body && !Array.isArray(body.changes) && body.data && typeof body.data === 'object') {
          body = body.data
        }

        if (!body || typeof body !== 'object') {
          self.fail('generic')
          return
        }

        self.detail = body
        self.loading = false
        self.loaded = true
        self.focusSummary()
      }, function (err) {
        if (seq !== self.seq) {
          return
        }

        var status = err && err.response ? err.response.status : null

        console.error('XOL-586: could not load the activity changes', status, err)
        self.fail(status === 403 ? 'forbidden' : (status === 404 ? 'notFound' : 'generic'))
      })
    },

    fail (kind) {
      this.error = kind
      this.loading = false
      this.loaded = true

      // An entry that is gone or off limits shows nothing of the one shown before.
      if (kind !== 'generic') {
        this.detail = null
      }
    },

    // Focus lands on the header once the dialog has opened.
    focusSummary () {
      var self = this

      if (!this.opened) {
        return
      }

      this.$nextTick(function () {
        var el = self.$refs.header || self.$refs.body

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    onOpen () {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
      this.detail = null
      this.loaded = false
    },

    onOpened () {
      this.opened = true
      this.focusSummary()
    },

    // Element closed itself (the ×, Esc, the backdrop): hand it to the parent. A close the
    // parent started (`visible` already false) is not announced twice.
    onDialogClose () {
      if (this.visible) {
        this.$emit('close')
      }
    },

    requestClose () {
      this.$emit('close')
    },

    // The dialog has closed: focus goes back to what opened it.
    onClosed () {
      var opener = this.opener

      this.opened = false
      this.opener = null

      if (opener && typeof opener.focus === 'function' && typeof document !== 'undefined' && document.body.contains(opener)) {
        opener.focus()
      }
    }
  }
}
</script>

<style scoped>
.changes { outline: none; }
.changes__coalesced { margin: 0 0 12px; font-size: 12px; line-height: 18px; color: #606266; }
.changes__coalesced .fa { margin-right: 6px; }
.changes__blocks.is-loading { min-height: 200px; }
.changes__retry { margin-top: 8px; }
.changes__none { margin: 0; font-size: 13px; color: #606266; }

.change-block { padding: 14px 0; border-top: 1px solid #EBEEF5; }
.change-block__label { margin: 0 0 8px; }
.change-block__back { margin-bottom: 8px; }
.change-block__back .el-tag { margin-right: 6px; }
.change-block__back .el-tag .fa { margin-right: 5px; }
.change-block__back small { font-size: 12px; color: #606266; }

.change-grid { display: grid; grid-template-columns: minmax(0, 1fr) 20px minmax(0, 1fr); gap: 8px; align-items: stretch; }
.change-grid.is-narrow { grid-template-columns: minmax(0, 1fr); }
.change-frame { border-radius: 4px; padding: 8px 12px; border-left: 3px solid; }
.change-frame.is-before { background: #FEF0F0; border-left-color: #F56C6C; }
.change-frame.is-after { background: #F0F9EB; border-left-color: #67C23A; }
.change-label { font-size: 12px; line-height: 16px; color: #606266; margin-bottom: 2px; }
.change-label .fa { margin-right: 5px; }
.is-before > .change-label .fa, .change-label .fa-minus { color: #F56C6C; }
.is-after > .change-label .fa, .change-label .fa-plus { color: #67C23A; }
.change-value { font-size: 14px; line-height: 20px; color: #303133; white-space: pre-wrap; word-break: break-word; }
.change-value.is-empty { font-style: italic; color: #909399; }
.change-arrow { display: flex; align-items: center; justify-content: center; color: #C0C4CC; font-size: 12px; }
.change-grid.is-narrow .change-arrow { height: 16px; }

.change-remarks { margin-bottom: 8px; }
.change-remarks.is-before ::v-deep .ro-remarks__frame { border-left: 3px solid #F56C6C; }
.change-remarks.is-after ::v-deep .ro-remarks__frame { border-left: 3px solid #67C23A; }

.change-group { margin-bottom: 8px; }
.change-group__head { font-size: 13px; line-height: 20px; font-weight: bold; color: #606266; }
.change-group__head .fa { margin-right: 6px; }
.change-list { list-style: none; margin: 4px 0 10px; padding: 2px 0 2px 12px; border-left: 3px solid #DCDFE6; font-size: 13px; line-height: 20px; color: #303133; word-break: break-word; }
.change-list li { padding: 2px 0; }
.change-list.is-scrolling { max-height: 16em; overflow: auto; }
.change-group.is-added .change-group__head .fa, .change-group.is-resubscribed .change-group__head .fa { color: #67C23A; }
.change-group.is-removed .change-group__head .fa, .change-group.is-unsubscribed .change-group__head .fa { color: #F56C6C; }
.change-group.is-ticks .change-group__head .fa { color: #909399; }
.change-group.is-added .change-list, .change-group.is-resubscribed .change-list { border-left-color: #67C23A; }
.change-group.is-removed .change-list, .change-group.is-unsubscribed .change-list { border-left-color: #F56C6C; }

.changes__footer { text-align: right; }
.changes__footer .is-block { width: 100%; }
</style>
